<template>
  <v-dialog
    v-model="visible"
    width="540"
    persistent
  >
    <v-card class="pa-2">
      <v-card-title class="text-h5 font-weight-bold text--primary pb-2">
        {{ $t('Как вам наш кабинет?') }}
      </v-card-title>
      <!-- text--primary у текста и подписей: у v-card-text цвет по умолчанию
           приглушённый (60% чёрного), из-за чего окно выглядело блёклым. -->
      <v-card-text class="text--primary">
        <p class="text-base mb-5">
          {{ $t('Заявка отправлена. Оцените, пожалуйста, работу кабинета — это займёт несколько секунд и поможет нам его улучшить.') }}
        </p>
        <!-- Полшага: клиент может поставить и половину звезды. -->
        <div class="d-flex justify-center mb-2">
          <v-rating
            v-model="rating"
            color="warning"
            background-color="grey lighten-1"
            half-increments
            hover
            length="5"
            size="48"
          ></v-rating>
        </div>
        <p
          v-if="attempted && !rating"
          class="text-sm text-center error--text mb-2"
        >
          {{ $t('Поставьте оценку') }}
        </p>
        <!-- Поле для текста показываем ТОЛЬКО после оценки и с вопросом под неё.
             Раньше оно висело сразу, с длинной обезличенной подписью, и все
             четверо первых отзывов пришли без единого слова. -->
        <template v-if="rating">
          <!-- Вопрос — такой же заголовок, как «Как вам наш кабинет?», только
               на ступень мельче; пояснение под ним — как вводный абзац. -->
          <p class="text-2xl font-weight-bold text--primary mt-8 mb-2">
            {{ question }}
          </p>
          <p
            v-if="hint"
            class="text-base mb-4"
          >
            {{ hint }}
          </p>
          <v-textarea
            ref="commentField"
            v-model="comment"
            :placeholder="$t('Ваш ответ')"
            rows="3"
            outlined
            autofocus
            hide-details="auto"
          ></v-textarea>
        </template>
      </v-card-text>
      <v-card-text
        v-if="failed"
        class="pt-0 pb-2"
      >
        <v-alert
          type="error"
          text
          dense
          class="mb-0"
        >
          {{ $t('Не получилось отправить. Попробуйте ещё раз — текст сохранён.') }}
        </v-alert>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          large
          :disabled="sending"
          @click="skip"
        >
          {{ $t('Не сейчас') }}
        </v-btn>
        <v-btn
          color="primary"
          large
          :loading="sending"
          @click="send"
        >
          {{ $t('Отправить') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import store from '@/store'
import { computed, getCurrentInstance, ref } from '@vue/composition-api'

// Окно с оценкой кабинета. Появляется один раз — после отправки заявки, и
// только если клиент отзыв ещё не оставлял (проверяется на сервере по логину).
export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const rating = ref(0)
    const comment = ref('')

    // Вопрос под оценку: у довольного клиента и у недовольного спрашивать
    // одно и то же бессмысленно.
    const question = computed(() => {
      if (rating.value >= 4.5) return vm.$t('Что понравилось больше всего?')
      if (rating.value >= 3.5) return vm.$t('Что мы могли бы улучшить?')

      return vm.$t('Что пошло не так?')
    })

    // Пояснение под вопросом. У довольного клиента его нет — лишний текст там
    // только мешает нажать «Отправить».
    const hint = computed(() => {
      if (rating.value >= 4.5) return ''
      if (rating.value >= 3.5) {
        return vm.$t('Даже мелочь пригодится: что показалось неудобным или чего не хватает.')
      }

      return vm.$t('Жаль, что не всё понравилось. Напишите, пожалуйста, что именно — постараемся исправить.')
    })
    const sending = ref(false)
    const attempted = ref(false)
    const failed = ref(false)

    const visible = computed({
      get: () => props.value,
      set: v => emit('input', v),
    })

    const close = () => emit('input', false)

    // «Не сейчас» — окно закрываем, но отзыв не записываем: спросим в
    // следующий раз, после следующей заявки.
    const skip = () => {
      close()
    }

    const send = () => {
      attempted.value = true
      if (!rating.value) return

      sending.value = true
      failed.value = false
      store
        .dispatch('feedback/submit', {
          ratingHalfStars: Math.round(rating.value * 2),
          comment: comment.value,
        })
        .then(ok => {
          sending.value = false
          if (ok) {
            close()

            return
          }

          // Не приняли — окно оставляем открытым. Раньше оно закрывалось в
          // любом случае, и отзыв пропадал незаметно и для клиента, и для нас.
          failed.value = true
        })
    }

    return {
      rating,
      comment,
      question,
      hint,
      sending,
      attempted,
      failed,
      visible,
      skip,
      send,
    }
  },
}
</script>
